"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { Logo } from "@/components/logo";
import { ThemeToggle } from "@/components/theme-toggle";
import { getToken } from "@/lib/api";
import { APP_VERSION } from "@/lib/version";

// Public marketing landing at "/". The app itself lives under /dashboard (auth-gated); this page is
// the front door for signed-out visitors. If a session already exists we don't hijack the page —
// we just swap the primary call-to-action to "Go to dashboard" so returning users have a fast path.

function Icon({ path, className = "h-6 w-6" }: { path: string; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} className={className} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d={path} />
    </svg>
  );
}

// Fade + rise a block into view the first time it scrolls near the viewport. Initial state is
// identical on server and client (hidden), so there's no hydration mismatch; it reveals after mount.
function Reveal({ children, className = "", delay = 0 }: { children: ReactNode; className?: string; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setShown(true); // no IO support: just show it
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          io.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`transition-all duration-700 ease-out ${shown ? "translate-y-0 opacity-100" : "translate-y-5 opacity-0"} ${className}`}
    >
      {children}
    </div>
  );
}

const FEATURES = [
  {
    title: "Single & bulk SMS",
    desc: "Fire off one message or a campaign to millions — paste numbers or upload a list, with a live segment and credit estimate.",
    path: "M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75"
  },
  {
    title: "Personalized merge",
    desc: "Upload a CSV and merge each recipient's data into the message with {{placeholders}} — statements, reminders, one-time codes.",
    path: "M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"
  },
  {
    title: "Real-time delivery",
    desc: "Track every message from queued to delivered with live status, delivery receipts and failure-reason insights.",
    path: "M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
  },
  {
    title: "Two-way inbox",
    desc: "Receive replies from handsets to your sender IDs, read them in a live inbox and reply in a click.",
    path: "M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z"
  },
  {
    title: "Contacts & segments",
    desc: "Save recipients once, file them into groups, and build dynamic segments that re-answer on every send.",
    path: "M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
  },
  {
    title: "API, keys & webhooks",
    desc: "Integrate sending into your own systems with self-service API keys, delivery webhooks and clear docs.",
    path: "M14.25 9.75L16.5 12l-2.25 2.25m-4.5 0L7.5 12l2.25-2.25M6 20.25h12A2.25 2.25 0 0020.25 18V6A2.25 2.25 0 0018 3.75H6A2.25 2.25 0 003.75 6v12A2.25 2.25 0 006 20.25z"
  }
];

const USE_CASES = [
  {
    title: "OTP & 2-factor",
    desc: "Deliver one-time passcodes and login verification in seconds, reliably.",
    path: "M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
  },
  {
    title: "Transaction alerts",
    desc: "Notify customers of payments, transfers and balance changes the moment they happen.",
    path: "M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
  },
  {
    title: "Marketing campaigns",
    desc: "Reach your whole audience with promotions, offers and announcements in one send.",
    path: "M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0018.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m0 0a23.91 23.91 0 001.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73"
  },
  {
    title: "Reminders",
    desc: "Cut no-shows with appointment, due-date, renewal and payment reminders.",
    path: "M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
  },
  {
    title: "Delivery updates",
    desc: "Keep buyers informed with order confirmations and dispatch notifications.",
    path: "M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12"
  },
  {
    title: "Service alerts",
    desc: "Warn customers about outages, maintenance windows and urgent updates.",
    path: "M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
  }
];

const TRUST = [
  "Approved sender IDs",
  "WaafiPay / EVC top-ups",
  "Delivery receipts",
  "Two-way SMS",
  "REST API & webhooks",
  "Scheduled campaigns"
];

const STEPS = [
  { n: "1", title: "Request access", desc: "Ask for an account and get approved by your Hormuud account manager." },
  { n: "2", title: "Get a sender ID", desc: "Register the “from” name your recipients will see, then top up credit." },
  { n: "3", title: "Start sending", desc: "Compose, schedule or bulk-send — and watch delivery in real time." }
];

const CHECK = "M4.5 12.75l6 6 9-13.5";

const PLANS = [
  {
    name: "Pay as you go",
    tag: "Best for getting started",
    highlight: false,
    lead: "Top up credit anytime",
    unit: "billed per SMS segment",
    features: ["Local SMS from 1 credit / segment", "National & international rates", "EVC / mobile-wallet top-ups", "No monthly commitment"],
    cta: "Get started"
  },
  {
    name: "Bulk packages",
    tag: "Most popular",
    highlight: true,
    lead: "Prepaid credit bundles",
    unit: "volume discounts built in",
    features: ["Discounted credit at volume", "Bulk & personalized campaigns", "Contacts, groups & segments", "Message templates & scheduling"],
    cta: "See packages"
  },
  {
    name: "Enterprise",
    tag: "Best for high volume",
    highlight: false,
    lead: "Custom & postpaid",
    unit: "tailored to your throughput",
    features: ["Postpaid billing & invoicing", "Dedicated sending throughput", "Multiple sender IDs & users", "API, webhooks & account manager"],
    cta: "Talk to us"
  }
];

const FAQS = [
  {
    q: "How do I get an account?",
    a: "Request one from the sign-in page. Your Hormuud account manager reviews it and shares your login details once approved."
  },
  {
    q: "What is a sender ID?",
    a: "It's the “from” name your recipients see (e.g. your brand). You register it in the portal and it's approved before you can send with it."
  },
  {
    q: "How does pricing work?",
    a: "Sending is credit-based. You top up credit (via EVC / mobile wallet) or buy prepaid packages, and each SMS segment draws credit at local, national or international rates."
  },
  {
    q: "Can I integrate it with my own system?",
    a: "Yes. Generate API keys in the portal and call the REST API to send, plus configure webhooks to receive delivery receipts and inbound replies."
  },
  {
    q: "Can I send bulk and personalized campaigns?",
    a: "Absolutely — upload or paste up to millions of numbers, merge per-recipient data with {{placeholders}}, schedule for later, and track delivery live."
  }
];

function ctaClasses(kind: "primary" | "secondary") {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 active:scale-[0.98]";
  return kind === "primary"
    ? `${base} bg-brand-600 text-pure-white shadow-sm hover:bg-brand-700 focus-visible:ring-brand-500`
    : `${base} border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50 hover:text-gray-900 focus-visible:ring-brand-500`;
}

// On-brand SMS mockup for the hero — decorative only.
function HeroMockup() {
  return (
    <div aria-hidden className="relative mx-auto w-full max-w-sm">
      {/* Portal-style card */}
      <div className="rounded-3xl border border-gray-200/70 bg-white p-5 shadow-card-hover">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-50 p-1 ring-1 ring-brand-100">
              <Logo className="h-full w-full" />
            </span>
            <span className="text-sm font-semibold text-gray-900">New campaign</span>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-success-50 px-2 py-0.5 text-[11px] font-medium text-success-700 ring-1 ring-inset ring-success-200 dark:bg-success-500/15 dark:text-success-300 dark:ring-success-500/30">
            <span className="h-1.5 w-1.5 rounded-full bg-success-500" /> Sending
          </span>
        </div>

        {/* Chat preview */}
        <div className="mt-4 rounded-2xl bg-gray-100 p-3 dark:bg-gray-500/10">
          <p className="mb-1.5 text-center text-[10px] font-medium uppercase tracking-wide text-gray-400">HORMUUD</p>
          <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-white px-3 py-2 text-[13px] text-gray-800 shadow-sm ring-1 ring-black/5">
            Hi Amina, your balance is 250 credits. Thank you for banking with us!
          </div>
        </div>

        {/* Delivery meter */}
        <div className="mt-4">
          <div className="mb-1 flex justify-between text-[11px] text-gray-500">
            <span>Delivered</span>
            <span className="tabular-nums">1,248,320 / 1,250,000</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-500/15">
            <div className="h-full rounded-full bg-gradient-to-r from-brand-500 to-accent-500" style={{ width: "99.9%" }} />
          </div>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2 text-center">
          {[
            { k: "99.9%", v: "Delivered" },
            { k: "GSM-7", v: "Encoding" },
            { k: "3s", v: "Avg. latency" }
          ].map((s) => (
            <div key={s.v} className="rounded-xl bg-gray-50 py-2 dark:bg-gray-500/10">
              <p className="text-sm font-bold text-gray-900">{s.k}</p>
              <p className="text-[10px] text-gray-500">{s.v}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Floating chips */}
      <div className="absolute -left-4 top-10 hidden rounded-xl border border-gray-200/70 bg-white px-3 py-2 shadow-dropdown sm:block">
        <p className="text-[10px] text-gray-400">Delivery receipt</p>
        <p className="text-xs font-semibold text-success-600 dark:text-success-400">✓ DELIVERED</p>
      </div>
      <div className="absolute -bottom-4 -right-3 hidden rounded-xl border border-gray-200/70 bg-white px-3 py-2 shadow-dropdown sm:block">
        <p className="text-[10px] text-gray-400">Reply received</p>
        <p className="text-xs font-semibold text-brand-600 dark:text-brand-400">“YES, confirm”</p>
      </div>
    </div>
  );
}

// Pricing: the credit-model tiers shown on the marketing page. Exact packages and live rates are
// shown in the portal after sign-in.
function PricingSection({ ctaHref }: { ctaHref: string }) {
  return (
    <section id="pricing" className="scroll-mt-16 border-t border-gray-100 bg-gray-50/60 py-20">
      <div className="mx-auto max-w-6xl px-5">
        <Reveal className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Simple, credit-based pricing</h2>
          <p className="mt-3 text-gray-600">
            Pay only for what you send. Top up credit or buy a prepaid package — every SMS segment draws
            credit at local, national or international rates.
          </p>
        </Reveal>

        <div className="mt-12 grid items-stretch gap-5 lg:grid-cols-3">
          {PLANS.map((p, i) => (
            <Reveal key={p.name} delay={i * 90} className="h-full">
              <div
                className={`flex h-full flex-col rounded-2xl border bg-white p-6 shadow-card ${
                  p.highlight ? "border-brand-300 ring-1 ring-brand-200 dark:ring-brand-500/30" : "border-gray-200/70"
                }`}
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-900">{p.name}</h3>
                  {p.highlight && (
                    <span className="rounded-full bg-brand-600 px-2.5 py-0.5 text-[11px] font-semibold text-pure-white">
                      Popular
                    </span>
                  )}
                </div>
                <p className="mt-3 text-2xl font-bold tracking-tight text-brand-600 dark:text-brand-400">{p.lead}</p>
                <p className="text-xs text-gray-500">{p.unit}</p>
                <ul className="mt-5 flex-1 space-y-2.5">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-gray-600">
                      <Icon path={CHECK} className="mt-0.5 h-4 w-4 shrink-0 text-success-500" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link href={ctaHref} className={`mt-6 w-full ${p.highlight ? ctaClasses("primary") : ctaClasses("secondary")}`}>
                  {p.cta}
                </Link>
              </div>
            </Reveal>
          ))}
        </div>

        <p className="mt-6 text-center text-xs text-gray-400">
          Exact packages and current rates are shown in the portal after you sign in.
        </p>
      </div>
    </section>
  );
}

export default function LandingPage() {
  const router = useRouter();
  // Reflect an existing session so returning users get a "Go to dashboard" shortcut. Read after
  // mount (localStorage is client-only) to keep the server-rendered markup stable. A signed-in
  // visitor is sent straight to the app — the landing is a front door for signed-out users.
  const [signedIn, setSignedIn] = useState(false);
  useEffect(() => {
    if (getToken()) {
      setSignedIn(true);
      // Arriving from the Hormuud Services hub (the MySMS tile lands on /?sso=1): show this
      // MySMS landing page first instead of bouncing straight to the dashboard. Normal returning
      // visitors still get the fast path to /dashboard.
      const fromHub = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("sso") === "1";
      if (fromHub) {
        window.history.replaceState(null, "", "/");
        return;
      }
      router.replace("/dashboard");
    }
  }, [router]);

  const primaryHref = signedIn ? "/dashboard" : "/login";
  const primaryLabel = signedIn ? "Go to dashboard" : "Sign in";

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-gray-100 bg-white/80 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-50 p-1.5 ring-1 ring-brand-100">
              <Logo className="h-full w-full" />
            </span>
            <span className="text-base font-bold tracking-tight">Hormuud SMS</span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <a href="#features" className="hidden rounded-md px-3 py-1.5 text-sm font-medium text-gray-600 transition hover:text-gray-900 md:block">
              Features
            </a>
            <a href="#pricing" className="hidden rounded-md px-3 py-1.5 text-sm font-medium text-gray-600 transition hover:text-gray-900 md:block">
              Pricing
            </a>
            <a href="#faq" className="hidden rounded-md px-3 py-1.5 text-sm font-medium text-gray-600 transition hover:text-gray-900 md:block">
              FAQ
            </a>
            <Link href="/guide" className="hidden rounded-md px-3 py-1.5 text-sm font-medium text-gray-600 transition hover:text-gray-900 md:block">
              Guide
            </Link>
            <ThemeToggle />
            <Link href={primaryHref} className={ctaClasses("primary") + " !px-4 !py-2"}>
              {primaryLabel}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute -top-24 left-1/2 h-96 w-[46rem] -translate-x-1/2 rounded-full bg-brand-100/60 blur-3xl dark:bg-brand-500/10" />
          <div className="absolute right-0 top-40 h-72 w-72 rounded-full bg-accent-100/50 blur-3xl dark:bg-accent-500/10" />
        </div>
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 pb-16 pt-16 sm:pt-20 lg:grid-cols-2 lg:gap-8">
          <div className="animate-slide-up text-center lg:text-left">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700 dark:border-brand-500/30 dark:bg-brand-500/10 dark:text-brand-300">
              <span className="h-1.5 w-1.5 rounded-full bg-success-500" />
              Enterprise SMS for Somalia &amp; beyond
            </span>
            <h1 className="mt-5 text-4xl font-bold leading-[1.08] tracking-tight sm:text-5xl md:text-6xl">
              Reach every customer,
              <span className="block bg-gradient-to-r from-brand-600 to-accent-500 bg-clip-text text-transparent dark:from-brand-400 dark:to-accent-400">
                one message at a time.
              </span>
            </h1>
            <p className="mx-auto mt-5 max-w-xl text-base text-gray-600 sm:text-lg lg:mx-0">
              Send single, bulk and personalized SMS, track delivery in real time, and manage your
              account — all from one fast, self-service portal.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row lg:justify-start">
              <Link href={primaryHref} className={ctaClasses("primary")}>
                {primaryLabel}
                <Icon path="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" className="h-4 w-4" />
              </Link>
              <Link href="/login" className={ctaClasses("secondary")}>
                Request an account
              </Link>
            </div>
            <p className="mt-4 text-xs text-gray-400">No setup fees · Pay as you send · Approved sender IDs</p>
          </div>

          <Reveal delay={120} className="lg:pl-6">
            <HeroMockup />
          </Reveal>
        </div>

        {/* Trust strip */}
        <div className="border-y border-gray-100 bg-gray-50/60">
          <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-x-8 gap-y-3 px-5 py-5">
            {TRUST.map((t) => (
              <span key={t} className="inline-flex items-center gap-2 text-sm font-medium text-gray-500">
                <Icon path={CHECK} className="h-4 w-4 text-success-500" />
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16">
        <div className="mx-auto grid max-w-5xl grid-cols-2 gap-4 px-5 sm:grid-cols-4">
          {[
            { k: "99.9%", v: "Delivery uptime" },
            { k: "1M+", v: "Recipients / campaign" },
            { k: "Real-time", v: "Delivery receipts" },
            { k: "24/7", v: "Sending" }
          ].map((s, i) => (
            <Reveal key={s.v} delay={i * 80}>
              <div className="rounded-2xl border border-gray-200/70 bg-white p-5 text-center shadow-card">
                <p className="text-2xl font-bold tracking-tight text-brand-600 dark:text-brand-400 sm:text-3xl">{s.k}</p>
                <p className="mt-1 text-xs text-gray-500">{s.v}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="scroll-mt-16 border-t border-gray-100 bg-gray-50/60 py-20">
        <div className="mx-auto max-w-6xl px-5">
          <Reveal className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Everything you need to message at scale</h2>
            <p className="mt-3 text-gray-600">One portal for sending, tracking, contacts, billing and integration.</p>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={(i % 3) * 90}>
                <div className="group h-full rounded-2xl border border-gray-200/70 bg-white p-6 shadow-card transition duration-200 hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-card-hover">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand-600 ring-1 ring-brand-100 transition group-hover:bg-brand-600 group-hover:text-pure-white dark:bg-brand-500/10 dark:text-brand-300 dark:ring-brand-500/20">
                    <Icon path={f.path} />
                  </span>
                  <h3 className="mt-4 text-base font-semibold text-gray-900">{f.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-gray-600">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section id="use-cases" className="scroll-mt-16 border-t border-gray-100 py-20">
        <div className="mx-auto max-w-6xl px-5">
          <Reveal className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">One platform, every message you send</h2>
            <p className="mt-3 text-gray-600">
              From security codes to marketing campaigns — teams across banking, retail, healthcare and
              government rely on SMS that actually arrives.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-x-8 gap-y-7 sm:grid-cols-2 lg:grid-cols-3">
            {USE_CASES.map((u, i) => (
              <Reveal key={u.title} delay={(i % 3) * 90}>
                <div className="flex gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-600 ring-1 ring-brand-100 dark:bg-brand-500/10 dark:text-brand-300 dark:ring-brand-500/20">
                    <Icon path={u.path} className="h-5 w-5" />
                  </span>
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">{u.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-gray-600">{u.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="scroll-mt-16 border-t border-gray-100 py-20">
        <div className="mx-auto max-w-6xl px-5">
          <Reveal className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Up and running in three steps</h2>
            <p className="mt-3 text-gray-600">From request to first message, without the paperwork drag.</p>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 90}>
                <div className="relative h-full rounded-2xl border border-gray-200/70 bg-white p-6 shadow-card">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-600 text-sm font-bold text-pure-white">
                    {s.n}
                  </div>
                  <h3 className="mt-4 text-base font-semibold text-gray-900">{s.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-gray-600">{s.desc}</p>
                  {i < STEPS.length - 1 && (
                    <Icon
                      path="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                      className="absolute -right-3 top-1/2 hidden h-5 w-5 -translate-y-1/2 text-gray-300 md:block"
                    />
                  )}
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-10 text-center">
            <Link href="/guide" className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-600 transition hover:gap-2.5 hover:text-brand-700 dark:text-brand-400">
              Read the full guide
              <Icon path="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Pricing (live catalogue with static fallback) */}
      <PricingSection ctaHref={primaryHref} />

      {/* FAQ */}
      <section id="faq" className="scroll-mt-16 py-20">
        <div className="mx-auto max-w-3xl px-5">
          <Reveal className="text-center">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Frequently asked questions</h2>
            <p className="mt-3 text-gray-600">Everything else you might want to know before you start.</p>
          </Reveal>
          <div className="mt-10 space-y-3">
            {FAQS.map((f) => (
              <details
                key={f.q}
                className="group rounded-2xl border border-gray-200/70 bg-white px-5 shadow-card [&_summary]:list-none"
              >
                <summary className="flex cursor-pointer items-center justify-between gap-4 py-4 text-sm font-semibold text-gray-900">
                  {f.q}
                  <Icon
                    path="M19.5 8.25l-7.5 7.5-7.5-7.5"
                    className="h-4 w-4 shrink-0 text-gray-400 transition group-open:rotate-180"
                  />
                </summary>
                <p className="pb-4 text-sm leading-relaxed text-gray-600">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="px-5 pb-20">
        <div className="relative mx-auto max-w-6xl overflow-hidden rounded-3xl bg-brand-700 px-6 py-14 text-center text-pure-white sm:px-12">
          <div aria-hidden className="pointer-events-none absolute inset-0 opacity-20">
            <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-accent-400 blur-3xl" />
            <div className="absolute -bottom-16 -left-16 h-64 w-64 rounded-full bg-brand-400 blur-3xl" />
          </div>
          <div className="relative">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Ready to send your first message?</h2>
            <p className="mx-auto mt-3 max-w-lg text-pure-white/80">
              Sign in to your account, or request access and your Hormuud account manager will get you set up.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                href={primaryHref}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-pure-white px-5 py-2.5 text-sm font-semibold text-brand-700 shadow-sm transition hover:bg-brand-50 active:scale-[0.98]"
              >
                {primaryLabel}
              </Link>
              <Link
                href="/login"
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-pure-white/40 px-5 py-2.5 text-sm font-semibold text-pure-white transition hover:bg-pure-white/10 active:scale-[0.98]"
              >
                Request an account
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-8">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-5 sm:flex-row">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Logo className="h-5 w-5" />
            <span>Hormuud SMS · Customer Portal</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-gray-400">
            <a href="#features" className="transition hover:text-gray-600">Features</a>
            <a href="#pricing" className="transition hover:text-gray-600">Pricing</a>
            <Link href="/guide" className="transition hover:text-gray-600">Guide</Link>
            <Link href="/login" className="transition hover:text-gray-600">Sign in</Link>
            <span>v{APP_VERSION}</span>
            <span>© {new Date().getFullYear()} Hormuud</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
